import { KeyboardArrowDown } from '@mui/icons-material';
import { Divider, LinkTypeMap, Menu, Link as MuiLink, PaperProps, Stack, StackProps, Typography, TypographyProps } from '@mui/material';
import { CrossDomainLink } from '@seeka-labs/converge-react';
import isAbsoluteUrl from 'is-absolute-url';
import React, { ElementType, useState } from 'react';
import { Link, LinkLabelAndDescription, LinkPropsBase, onLinkClick } from '../link';

export type LinkGroupProps = LinkPropsBase & {
    links: LinkPropsBase[] | null | undefined
    withDropdown?: boolean
    enableIcons?: boolean
    alignLeft?: boolean
    headerComponent?: string
    forceLink?: boolean
    LinkProps?: LinkTypeMap['props']
    LinkGroupItemsTypographyProps?: TypographyProps
    LinkGroupHeaderTypographyProps?: TypographyProps
    LinkGroupHeaderLinkProps?: LinkTypeMap['props']
    LinkGroupItemsStackProps?: StackProps
    LinkDropdownStackProps?: StackProps
    LinkDropdownPaperProps?: PaperProps
    disableGroupDropDownIcon?: boolean
    onClick?: () => void
}

export type LinkGroupItemsProps = {
    links: LinkPropsBase[] | null | undefined,
    alignLeft?: boolean,
    relativeLinkComponent?: ElementType,
    forceLink?: boolean,
    LinkProps?: LinkTypeMap['props'],
    LinkGroupItemsTypographyProps?: TypographyProps,
    LinkGroupItemsStackProps?: StackProps
    onClick?: () => void
}

function LinkGroupItems({
    links,
    alignLeft,
    relativeLinkComponent,
    forceLink,
    LinkProps,
    LinkGroupItemsTypographyProps,
    LinkGroupItemsStackProps,
    onClick
}: LinkGroupItemsProps) {
    if (!links || links.length === 0) return <></>
    return <Stack
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
        gap={{ xs: 0, sm: 0, md: alignLeft ? 0 : 2 }}
        {...LinkGroupItemsStackProps}
    >
        {links.map((link, i) => {
            const linkElement = <Link
                relativeLinkComponent={relativeLinkComponent}
                key={i}
                forceLink={forceLink}
                mobileLabel={link.mobileLabel}
                label={link.label}
                url={link.url}
                LinkProps={LinkProps}
                onClick={onClick}
                sx={!alignLeft ? {
                    width: { xs: "100%", sm: "100%", md: "inherit" }, // SEO
                    minHeight: { xs: "48px", sm: "48px", md: "inherit" }, // SEO
                    ...LinkProps?.sx
                } : { ml: 5, ...LinkProps?.sx }}
            />;
            return (
                LinkGroupItemsTypographyProps ? <Typography {...LinkGroupItemsTypographyProps} key={i}>{linkElement}</Typography> : linkElement
            )
        })}
    </Stack>
}

export function LinkGroup({
    label,
    description,
    url,
    links,
    withDropdown,
    alignLeft,
    relativeLinkComponent,
    headerComponent,
    forceLink,
    LinkProps,
    LinkGroupItemsTypographyProps,
    LinkGroupHeaderTypographyProps,
    LinkGroupHeaderLinkProps,
    LinkGroupItemsStackProps,
    disableGroupDropDownIcon,
    LinkDropdownStackProps,
    enableIcons,
    LinkDropdownPaperProps,
    onClick
}: LinkGroupProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleCloseAndClick = (urlLink: string) => {
        onLinkClick(urlLink, undefined, onClick)
        setAnchorEl(null);
    };

    const hasSubLinks = links && links.length > 0
    const showHeaderButton = (url || (hasSubLinks && withDropdown));

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>, urlLink: string) => {
        setAnchorEl(event.currentTarget);
        onLinkClick(urlLink, event, onClick)
    };

    return (
        <Stack gap={{ xs: 0, sm: 0, md: alignLeft ? 0 : 2 }}>
            {label && <Typography variant={headerComponent as any} {...LinkGroupHeaderTypographyProps as any} component={"div"} sx={!headerComponent ? { fontSize: "1rem", fontWeight: 700, ...LinkGroupHeaderTypographyProps?.sx } : LinkGroupHeaderTypographyProps?.sx}>
                {showHeaderButton && <Link LinkProps={{ ...LinkGroupHeaderLinkProps, underline: 'none' }} forceLink={!withDropdown || forceLink} sx={{ textTransform: 'inherit', fontWeight: 'inherit', fontSize: 'inherit', ...LinkGroupHeaderLinkProps?.sx }} relativeLinkComponent={!hasSubLinks ? relativeLinkComponent : undefined} url={url} label={label} description={description} endIcon={!disableGroupDropDownIcon && withDropdown ? <KeyboardArrowDown /> : undefined} onClick={(e) => {
                    if (withDropdown) handleClick(e, url)
                    else onLinkClick(url, e, onClick)
                }} />}
                {!showHeaderButton ? label : undefined}
            </Typography>}

            {links && links.length > 0 && (
                withDropdown ? (
                    <Menu
                        id={`menu-${label}`}
                        anchorEl={anchorEl}
                        open={open}
                        disableScrollLock
                        onClose={handleClose}
                        slotProps={{
                            paper: LinkDropdownPaperProps
                        }}
                        MenuListProps={{
                            'aria-labelledby': `menu-button-${label}`,
                        }}
                    >
                        <Stack gap={2}
                            divider={<Divider />}
                            {...LinkDropdownStackProps} sx={{ pl: 2, pr: 2, pb: 2, pt: 2, ...LinkDropdownStackProps?.sx }}>
                            {links.map((link, i) => {
                                const isAbsUrl = link.url && isAbsoluteUrl(link.url)

                                return (
                                    relativeLinkComponent && !isAbsUrl ?
                                        (
                                            <MuiLink component={relativeLinkComponent} underline='none' href={link.url || undefined} to={link.url || undefined} key={i}>
                                                {/* <MenuItem onClick={handleClose}>{link.label}</MenuItem> */}
                                                <LinkLabelAndDescription label={link.label} description={link.description} mobileLabel={link.mobileLabel} onClick={() => handleCloseAndClick(link.url)} icon={enableIcons && link.dropdownItemIcon ? link.dropdownItemIcon : undefined} transitionIndex={i} />
                                            </MuiLink>
                                        ) : (
                                            <CrossDomainLink key={i}>
                                                <MuiLink underline='none' href={link.url || undefined} target={isAbsUrl ? '_blank' : undefined}>
                                                    {/* <MenuItem onClick={handleClose}>{link.label}</MenuItem> */}
                                                    <LinkLabelAndDescription label={link.label} description={link.description} mobileLabel={link.mobileLabel} onClick={() => handleCloseAndClick(link.url)} icon={enableIcons && link.dropdownItemIcon ? link.dropdownItemIcon : undefined} transitionIndex={i} />
                                                </MuiLink>
                                            </CrossDomainLink>
                                        )

                                )

                            })}
                        </Stack>
                    </Menu>
                ) : (
                    <LinkGroupItems onClick={onClick} LinkGroupItemsStackProps={LinkGroupItemsStackProps} LinkGroupItemsTypographyProps={LinkGroupItemsTypographyProps} forceLink={forceLink} LinkProps={LinkProps} links={links} alignLeft={alignLeft} relativeLinkComponent={relativeLinkComponent} />
                )
            )}
        </Stack>
    )
}
